import React from 'react';
import { highlightName, highlightText } from '../../util';
import styles from './selector.module.scss';
import Avatar from '@web-common/components/UI/Avatar';
import IconCard from '@web-common/components/UI/IconCard';
import { EllipsisText } from '@web-common/components/UI';
import { CustomerLabelByRole } from '@web-mail/components/ReadMail/component/CustomerLabel';
const highlightEmail = (textToSearch = '', searchTerm, hitStyle) => {
    let html = textToSearch;
    if (searchTerm && html) {
        html = highlightText(html, searchTerm, hitStyle);
    }
    return html;
};
const ORG_SUFFIX_WIDTH_RATIO = 0.5;
const DEFAULT_ORG_FONT = '12px PingFang SC';
let measureCanvas = null;
const getMeasureContext = () => {
    if (typeof document === 'undefined') {
        return null;
    }
    if (!measureCanvas) {
        measureCanvas = document.createElement('canvas');
    }
    return measureCanvas.getContext('2d');
};
const measureTextWidth = (text, font) => {
    const ctx = getMeasureContext();
    if (!ctx) {
        return text.length;
    }
    ctx.font = font;
    return ctx.measureText(text).width;
};
const getFallbackFooterLength = (text) => {
    if (text.length <= 1) {
        return 0;
    }
    return Math.max(1, Math.floor(text.length / 2));
};
const computeFooterLengthByWidth = (text, availableWidth, font) => {
    if (!text) {
        return 0;
    }
    if (text.length <= 1) {
        return 0;
    }
    if (!Number.isFinite(availableWidth) || availableWidth <= 0) {
        return getFallbackFooterLength(text);
    }
    const fullWidth = measureTextWidth(text, font);
    if (fullWidth <= availableWidth) {
        return 0;
    }
    const maxSuffixWidth = Math.max(availableWidth * ORG_SUFFIX_WIDTH_RATIO, 1);
    let low = 1;
    let high = text.length - 1;
    let best = 1;
    while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const suffix = text.slice(text.length - mid);
        const suffixWidth = measureTextWidth(suffix, font);
        if (suffixWidth <= maxSuffixWidth) {
            best = mid;
            low = mid + 1;
        }
        else {
            high = mid - 1;
        }
    }
    return best;
};
const OptionItem = props => {
    const { item, search } = props;
    if (!item) {
        return null;
    }
    const { hitQuery = [], name: contactName = '', position = [], email, type, id, avatar, remark } = item;
    const firstPos = position === null || position === void 0 ? void 0 : position[0];
    const orgArr = Array.isArray(firstPos) ? firstPos : typeof firstPos === 'string' ? [firstPos] : [];
    const org = orgArr.join('-');
    const htmlName = highlightName(contactName, hitQuery, search, styles.hitText);
    const highlightedEmail = highlightEmail(email || '', search, styles.hitText);
    const htmlEmail = htmlName ? `&lt;${highlightedEmail}&gt;` : highlightedEmail;
    const isCustomer = type === 'customer';
    let titleLabelRole;
    if (type === 'customer' || type === 'clue') {
        titleLabelRole = item.customerRole;
    }
    const [orgFooterLength, setOrgFooterLength] = React.useState(() => getFallbackFooterLength(org));
    const orgWrapRef = React.useRef(null);
    const updateOrgFooterLength = React.useCallback(() => {
        if (!org || typeof window === 'undefined') {
            return;
        }
        const wrap = orgWrapRef.current;
        if (!wrap) {
            return;
        }
        const width = wrap.clientWidth;
        const computedStyle = window.getComputedStyle(wrap);
        const font = (computedStyle === null || computedStyle === void 0 ? void 0 : computedStyle.font) || DEFAULT_ORG_FONT;
        const nextFooterLength = computeFooterLengthByWidth(org, width, font);
        setOrgFooterLength(prev => (prev === nextFooterLength ? prev : nextFooterLength));
    }, [org]);
    React.useEffect(() => {
        setOrgFooterLength(getFallbackFooterLength(org));
    }, [org]);
    React.useEffect(() => {
        if (!org) {
            return;
        }
        updateOrgFooterLength();
    }, [org, updateOrgFooterLength]);
    React.useEffect(() => {
        if (!org || typeof window === 'undefined') {
            return;
        }
        const wrap = orgWrapRef.current;
        if (!wrap || typeof ResizeObserver === 'undefined') {
            return;
        }
        let rafId = null;
        const observer = new ResizeObserver(() => {
            if (rafId !== null) {
                window.cancelAnimationFrame(rafId);
            }
            rafId = window.requestAnimationFrame(() => {
                updateOrgFooterLength();
            });
        });
        observer.observe(wrap);
        return () => {
            if (rafId !== null) {
                window.cancelAnimationFrame(rafId);
            }
            observer.disconnect();
        };
    }, [org, updateOrgFooterLength]);
    return (React.createElement("div", { className: styles.optionItem, key: id + '_' + email },
        React.createElement("div", { className: styles.optionAvatarWrap }, !titleLabelRole ? (React.createElement(Avatar, { size: 20, item: {
                contact: {
                    id,
                    avatar,
                    contactName,
                    email,
                },
            } })) : (React.createElement(IconCard, { type: isCustomer ? 'crmClient' : 'hintClient' }))),
        React.createElement("span", { className: styles.optionContactName }, contactName),
        React.createElement("span", { className: styles.optionContactName, style: email && contactName ? { marginLeft: '8px' } : {} }, email),
        org ? (React.createElement("div", { className: styles.optionOrgMiddle, ref: orgWrapRef },
            React.createElement(EllipsisText, { text: org, footerLength: orgFooterLength, tipsZIndex: 10001 }))) : null,
        remark && (React.createElement("span", { className: styles.optionOrg, style: { marginLeft: '8px' } },
            "\u5907\u6CE8\uFF1A",
            remark)),
        titleLabelRole && React.createElement(CustomerLabelByRole, { role: titleLabelRole, isContact: true, style: { marginLeft: 8 } })));
};
export default OptionItem;
